import { useEffect, useState } from 'react';
import { Check, Copy, Eye, EyeOff, KeyRound, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { fetchRouterCredentials } from '@/services/provisioning';
import type { RouterCredentials } from '@asi/shared';

interface RouterCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Gateway row UUID (or router code) to load when the dialog opens. Omit to start with the search box. */
  lookup?: string;
}

/**
 * Shows a router's derived admin/WiFi credentials. Credentials are fetched on demand
 * (never cached in the page) and every lookup is audit-logged by the gateway.
 */
export function RouterCredentialsDialog({ open, onOpenChange, lookup }: RouterCredentialsDialogProps) {
  const [creds, setCreds] = useState<RouterCredentials | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [rotationOpen, setRotationOpen] = useState(false);

  const load = async (code: string) => {
    setLoading(true);
    setError('');
    setRevealed(false);
    setRotationOpen(false);
    try {
      setCreds(await fetchRouterCredentials(code));
    } catch (e) {
      setCreds(null);
      setError(e instanceof Error ? e.message : 'No se pudieron obtener las credenciales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && lookup) load(lookup);
    if (!open) { setCreds(null); setError(''); setQuery(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lookup]);

  const copy = async (key: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const secret = (key: string, label: string, value: string) => (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <code className="font-mono font-medium select-all">{revealed ? value : '••••-••••-••••-••••'}</code>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(key, value)} title="Copiar">
          {copied === key ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Credenciales del router
          </DialogTitle>
          <DialogDescription>
            Cada router tiene su propia contraseña. Esta consulta queda registrada con tu usuario.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (query.trim()) load(query); }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Otro router: ASI-XXXXXXXX (ver WinBox → Neighbors → Identity)"
            className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm font-mono"
          />
          <Button type="submit" size="sm" variant="outline" disabled={loading || !query.trim()}>
            <Search className="h-4 w-4" />
          </Button>
        </form>

        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && <p className="text-sm text-destructive">{error}</p>}

        {creds && !loading && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Código</span>
                <code className="font-mono font-semibold">{creds.routerCode}</code>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Pertenece a</span>
                <span className="text-right">{creds.owner?.label ?? 'No encontrado en la base de datos'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Usuario</span>
                <code className="font-mono">{creds.adminUser}</code>
              </div>
              {secret('admin', 'Contraseña admin', creds.adminPassword)}
              {secret('wifi', 'Clave WiFi (ASI-Gateway)', creds.wifiPassphrase)}
              <Button size="sm" variant="ghost" className="w-full" onClick={() => setRevealed((v) => !v)}>
                {revealed ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {revealed ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>

            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 text-xs overflow-hidden">
              <button
                type="button"
                onClick={() => setRotationOpen((v) => !v)}
                className="w-full px-3 py-2 text-left font-medium text-yellow-600 hover:bg-yellow-500/10"
              >
                ¿Router instalado con la clave anterior? Comandos para actualizarlo
              </button>
              {rotationOpen && (
                <div className="px-3 pb-3 space-y-2 border-t border-yellow-500/20 pt-2">
                  <p className="text-muted-foreground">
                    Entrar con la clave anterior (WinBox vía EC2 o en sitio), abrir New Terminal y pegar:
                  </p>
                  <div className="relative">
                    <pre className="bg-muted rounded p-2 text-[11px] whitespace-pre-wrap break-all">
                      {revealed ? creds.rotationCommands : creds.rotationCommands.replace(/password="[^"]+"/, 'password="••••"')}
                    </pre>
                    <Button
                      size="icon" variant="ghost" className="absolute top-1 right-1 h-7 w-7"
                      onClick={() => copy('rotation', creds.rotationCommands)} title="Copiar comandos"
                    >
                      {copied === 'rotation' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
